import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Calendar, CheckCircle2, Video, XCircle } from "lucide-react";
import {
  VentureCard,
  VentureCardHeader,
  VentureCardTitle,
  VentureCardContent,
} from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { supabase } from "@/integrations/supabase/client";

function startOfIsoWeek(): string {
  const now = new Date();
  const dow = now.getUTCDay(); // 0=Sun
  const diff = (dow + 6) % 7; // days since Monday
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - diff));
  return d.toISOString();
}

export default function Home() {
  const weekStart = startOfIsoWeek();

  // Closer row — establishes identity + Fathom connection status.
  // RLS auto-scopes; .eq(user_id, auth.uid()) is also applied as a defensive filter.
  const { data: closer, isLoading: closerLoading } = useQuery({
    queryKey: ["closer-self-row"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase
        .from("closers")
        .select("id, user_id, fathom_api_key_vault_ref")
        .eq("user_id", user.id)
        .maybeSingle();
      return data;
    },
    staleTime: 60_000,
  });

  // Demos booked this week — RLS enforces own-closer scope.
  const { data: bookedCount } = useQuery({
    queryKey: ["closer-home-booked", weekStart],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("demos")
        .select("id", { count: "exact", head: true })
        .gte("demo_date", weekStart);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 60_000,
  });

  // Demos held this week
  const { data: heldCount } = useQuery({
    queryKey: ["closer-home-held", weekStart],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("demos")
        .select("id", { count: "exact", head: true })
        .gte("demo_date", weekStart)
        .eq("status", "held");
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 60_000,
  });

  const fathomConnected = !!closer?.fathom_api_key_vault_ref;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Closer Home</h1>
        <p className="text-muted-foreground">Your demos and integrations this week.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-secondary" />
              Demos Booked (this week)
            </VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <p className="text-3xl font-bold">
              {bookedCount ?? (closerLoading ? "…" : 0)}
            </p>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Demos Held (this week)
            </VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <p className="text-3xl font-bold">
              {heldCount ?? (closerLoading ? "…" : 0)}
            </p>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-blue-500" />
              Fathom
            </VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            {fathomConnected ? (
              <BadgeVenture variant="success">Connected</BadgeVenture>
            ) : (
              <div className="space-y-2">
                <BadgeVenture variant="warning">
                  <XCircle className="w-3 h-3 inline-block mr-1" />
                  Not Connected
                </BadgeVenture>
                <div>
                  <Link
                    to="/closer/fathom"
                    className="text-sm text-secondary hover:underline"
                  >
                    Connect Fathom →
                  </Link>
                </div>
              </div>
            )}
          </VentureCardContent>
        </VentureCard>
      </div>
    </div>
  );
}
